import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { OrdersWithPayment } from 'src/app/models/order';
import { MenuPhotoSubmissionService } from 'src/app/_services/menu-photo-submission.service';

interface ShareableItem {
  menuItemGroupId: number;
  label: string;
}

/**
 * "Love your food? Share a photo and earn points" — shown on the order status
 * screen once an order is in. Picking an item opens the camera (or gallery),
 * and the photo goes to the restaurant's review queue; points land when they
 * approve it.
 */
@Component({
  selector: 'app-share-photo-card',
  templateUrl: './share-photo-card.component.html',
  styleUrls: ['./share-photo-card.component.scss']
})
export class SharePhotoCardComponent implements OnInit, OnDestroy {

  @Input() orderWithPayment: OrdersWithPayment;

  @ViewChild('cameraInput') cameraInput: ElementRef<HTMLInputElement>;
  @ViewChild('galleryInput') galleryInput: ElementRef<HTMLInputElement>;

  /** Read from the store's customer_photo_points, falling back to the service default. */
  get pointsPerPhoto(): number {
    return (this.orderWithPayment && this.orderWithPayment.store
            && this.orderWithPayment.store.customer_photo_points)
           || MenuPhotoSubmissionService.DEFAULT_POINTS_PER_PHOTO;
  }

  items: ShareableItem[] = [];
  submittedItemIds: number[] = [];
  pickingFor: ShareableItem | null = null;
  uploadingItemId: number | null = null;

  private pollTimer: any;

  constructor(
    private submissionService: MenuPhotoSubmissionService,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit() {
    this.items = this.buildItems();
    this.submittedItemIds = this.submissionService.submittedItemIds(this.orderHash);
    this.startStatusPolling();
  }

  ngOnDestroy() {
    if (this.pollTimer) clearInterval(this.pollTimer);
  }

  get orderHash(): string {
    return (this.orderWithPayment && this.orderWithPayment.bot_order
      && this.orderWithPayment.bot_order.order_hash) || '';
  }

  get storeId(): number | string | null {
    return (this.orderWithPayment && this.orderWithPayment.store
      && this.orderWithPayment.store.id) || null;
  }

  /** Hide the whole card when there is nothing shareable or no order to tie it to */
  get isVisible(): boolean {
    return !!(this.orderHash && this.storeId && this.items.length);
  }

  get remainingCount(): number {
    return this.items.filter(item => !this.hasSubmitted(item)).length;
  }

  hasSubmitted(item: ShareableItem): boolean {
    return this.submittedItemIds.indexOf(item.menuItemGroupId) !== -1;
  }

  startPicking(item: ShareableItem) {
    this.pickingFor = item;
  }

  cancelPicking() {
    this.pickingFor = null;
  }

  openCamera() {
    this.cameraInput.nativeElement.click();
  }

  openGallery() {
    this.galleryInput.nativeElement.click();
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    input.value = '';
    if (!file || !this.pickingFor) { return; }
    this.upload(this.pickingFor, file);
  }

  private upload(item: ShareableItem, file: File) {
    const phoneNumber = (this.orderWithPayment.bot_order
      && this.orderWithPayment.bot_order.phone_number) || '';
    this.pickingFor = null;
    this.uploadingItemId = item.menuItemGroupId;

    this.convertToJpeg(file).then(convertedFile => {
      this.submissionService
        .submit(this.storeId, this.orderHash, item.menuItemGroupId, phoneNumber, convertedFile)
        .subscribe({
          next: () => {
            this.uploadingItemId = null;
            this.markSubmitted(item);
            this.snackBar.open(
              `Photo sent to the restaurant — ${this.pointsPerPhoto} points once they approve it`,
              '', { duration: 4000 });
          },
          error: (error) => {
            this.uploadingItemId = null;
            // 409 means a photo for this item is already in their queue, which is
            // the state we wanted — show it as sent rather than as a failure.
            if (error && error.status === 409) {
              this.submissionService.remember(this.orderHash, item.menuItemGroupId);
              this.markSubmitted(item);
              this.snackBar.open('You already shared a photo of that dish', '', { duration: 3000 });
              return;
            }
            const message = (error && error.error && error.error.msg)
              || 'Could not send that photo. Please try again.';
            this.snackBar.open(message, 'OK', { duration: 5000 });
          }
        });
    }).catch(() => {
      this.uploadingItemId = null;
      this.snackBar.open('Could not process that photo. Please try again.', 'OK', { duration: 5000 });
    });
  }

  /** Convert any image (including HEIC from iPhones) to JPEG via canvas.
   *  createImageBitmap auto-handles EXIF rotation so portrait photos
   *  won't come out sideways once the orientation tag is stripped. */
  private convertToJpeg(file: File): Promise<File> {
    if (file.type === 'image/jpeg' || file.type === 'image/jpg') {
      return Promise.resolve(file);
    }
    return createImageBitmap(file).then(bitmap => {
      const canvas = document.createElement('canvas');
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(bitmap, 0, 0);
      bitmap.close();
      return new Promise<File>((resolve, reject) => {
        canvas.toBlob(blob => {
          if (blob) {
            resolve(new File([blob], 'photo.jpg', { type: 'image/jpeg' }));
          } else {
            reject(new Error('Canvas toBlob failed'));
          }
        }, 'image/jpeg', 0.85);
      });
    });
  }

  private markSubmitted(item: ShareableItem) {
    if (!this.hasSubmitted(item)) {
      this.submittedItemIds = this.submittedItemIds.concat([item.menuItemGroupId]);
    }
  }

  /** Poll for status updates on submitted photos every 30 seconds */
  private startStatusPolling() {
    if (!this.storeId || !this.submittedItemIds.length) return;

    const phone = (this.orderWithPayment.bot_order && this.orderWithPayment.bot_order.phone_number) || '';
    if (!phone) return;

    this.pollTimer = setInterval(() => {
      this.submissionService.checkStatuses(this.storeId, phone).subscribe(subs => {
        subs.forEach(s => {
          if (s.status === 'approved') {
            // Mark approved items — the template can react to this
          }
        });
      });
    }, 30000); // Poll every 30 seconds
  }

  /** One entry per distinct ordered item that maps to a menu item group */
  private buildItems(): ShareableItem[] {
    const items = (this.orderWithPayment && this.orderWithPayment.items) || [];
    const seen: { [id: number]: boolean } = {};
    const shareable: ShareableItem[] = [];

    items.forEach((entry: any) => {
      const menuItem = entry && entry.menu_item;
      const groupId = menuItem && menuItem.menu_item_group;
      if (!groupId || seen[groupId]) { return; }
      seen[groupId] = true;
      const baseName = menuItem.display_name || menuItem.name || 'your dish';
      const portion = menuItem.portion;
      shareable.push({
        menuItemGroupId: groupId,
        label: (portion && portion !== 'Standard') ? `${portion} ${baseName}` : baseName
      });
    });

    return shareable;
  }
}
